// Lightweight Markdown renderer tailored for Territoria Wiki content.
// Supports: headings, bold, italic, code (inline + block), links, lists, tables, blockquotes, hr.
// SECURITY: Output is sanitized via DOMPurify to prevent XSS through user-authored markdown
// (raw HTML, javascript: URLs, dangerous attributes, etc.).
import DOMPurify from "dompurify";

function escapeHtml(str) {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Allow only safe URL schemes (block javascript:, data:, vbscript:, etc.)
function safeUrl(u) {
  const trimmed = String(u).trim();
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) return trimmed;
  if (/^(https?:|mailto:)/i.test(trimmed)) return trimmed;
  return "#";
}

function inline(text) {
  // Escape ALL angle brackets first to prevent raw-HTML injection
  text = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Code (inline)
  text = text.replace(/`([^`]+)`/g, (_, c) => `<code>${c}</code>`);
  // Bold
  text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Italic
  text = text.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "<em>$1</em>");
  // Links [text](url) — sanitize URL scheme
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, t, u) => {
    const url = safeUrl(u);
    const isInternal = url.startsWith("/") || url.startsWith("#");
    const target = isInternal ? "" : ' target="_blank" rel="noopener noreferrer"';
    return `<a href="${url}"${target}>${t}</a>`;
  });
  return text;
}

export function renderMarkdown(md) {
  if (!md) return "";
  const lines = md.split(/\r?\n/);
  const out = [];
  let i = 0;

  const flushParagraph = (buf) => {
    if (buf.length) {
      out.push(`<p>${inline(buf.join(" "))}</p>`);
      buf.length = 0;
    }
  };

  let paraBuf = [];

  while (i < lines.length) {
    const line = lines[i];

    // Heading
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      flushParagraph(paraBuf);
      out.push(`<h${h[1].length}>${inline(h[2])}</h${h[1].length}>`);
      i++; continue;
    }

    // HR
    if (/^---+$/.test(line.trim())) {
      flushParagraph(paraBuf);
      out.push("<hr/>"); i++; continue;
    }

    // Blockquote
    if (line.startsWith("> ")) {
      flushParagraph(paraBuf);
      const buf = [];
      while (i < lines.length && lines[i].startsWith("> ")) {
        buf.push(lines[i].slice(2));
        i++;
      }
      out.push(`<blockquote>${inline(buf.join(" "))}</blockquote>`);
      continue;
    }

    // Code block ```
    if (line.startsWith("```")) {
      flushParagraph(paraBuf);
      i++;
      const buf = [];
      while (i < lines.length && !lines[i].startsWith("```")) { buf.push(lines[i]); i++; }
      i++; // skip closing fence
      out.push(`<pre><code>${escapeHtml(buf.join("\n"))}</code></pre>`);
      continue;
    }

    // Table
    if (line.includes("|") && i + 1 < lines.length && /^[\s|:\-]+$/.test(lines[i + 1])) {
      flushParagraph(paraBuf);
      const header = line.split("|").map(s => s.trim()).filter(Boolean);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|")) {
        rows.push(lines[i].split("|").map(s => s.trim()).filter(Boolean));
        i++;
      }
      const thead = `<thead><tr>${header.map(h => `<th>${inline(h)}</th>`).join("")}</tr></thead>`;
      const tbody = `<tbody>${rows.map(r => `<tr>${r.map(c => `<td>${inline(c)}</td>`).join("")}</tr>`).join("")}</tbody>`;
      out.push(`<div class="table-wrap"><table>${thead}${tbody}</table></div>`);
      continue;
    }

    // List
    if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      flushParagraph(paraBuf);
      const ordered = /^\s*\d+\.\s+/.test(line);
      const tag = ordered ? "ol" : "ul";
      const items = [];
      while (i < lines.length && (/^\s*[-*]\s+/.test(lines[i]) || /^\s*\d+\.\s+/.test(lines[i]))) {
        items.push(lines[i].replace(/^\s*(?:[-*]|\d+\.)\s+/, ""));
        i++;
      }
      out.push(`<${tag}>${items.map(it => `<li>${inline(it)}</li>`).join("")}</${tag}>`);
      continue;
    }

    // Empty line → break paragraph
    if (!line.trim()) {
      flushParagraph(paraBuf);
      i++; continue;
    }

    paraBuf.push(line);
    i++;
  }
  flushParagraph(paraBuf);

  // Sanitize the final HTML before returning — strips any residual unsafe
  // markup, dangerous attributes, or javascript: URLs that slipped through.
  return DOMPurify.sanitize(out.join("\n"), {
    ALLOWED_TAGS: [
      "h1", "h2", "h3", "h4", "h5", "h6",
      "p", "a", "strong", "em", "code", "pre",
      "ul", "ol", "li", "blockquote", "hr", "br",
      "table", "thead", "tbody", "tr", "th", "td", "div", "mark"
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
    ALLOW_DATA_ATTR: false,
  });
}
